import gql from 'graphql-tag';
import type { DocumentNode } from 'graphql';
import { useQuery } from '@apollo/react-hooks';
import {
	type GQLExtensionContextExtensionsByTypeVariables,
	type GQLExtensionContextsVariables,
	type GQLExtensionContextsFilter,
    GQLExtensionContextsFilterType,
} from '../graphql/types';
import type { ApolloClient } from 'apollo-client';
import { fg } from '@atlaskit/platform-feature-flags';

export const extensionByTypeQuery: DocumentNode = gql`
	query forge_ui_extensionList($contextIds: [ID!]!, $filter: [ExtensionContextsFilter!]! ) {
        extensionContexts(contextIds: $contextIds) {
            extensions(filter: $filter) {
                appId
                properties
				key
            }
        }
    }
`;

interface QueryData {
	extensionContexts: {
		extensions: {
			appId: string;
			key: string;
			properties: any;
		}[];
	}[];
}

interface QueryVariables {
	contextIds: GQLExtensionContextsVariables['contextIds'];
	filter?: GQLExtensionContextsFilter[];
	type?: GQLExtensionContextExtensionsByTypeVariables['type'];
}

const CORE_EVENT = "core:event";
const CLIENT_EVENTS = "client-events";
const CLIENT_EVENT = "client-event";

const useAllowedRecipients = (contextId: string, appId?: string, client?: ApolloClient<any>) => {
	const { data } = useQuery<QueryData, QueryVariables>(
		extensionByTypeQuery,
        {
            variables: {
                contextIds: [contextId],
                filter: [
                    {
                        type: GQLExtensionContextsFilterType.ExtensionType,
                        value: CORE_EVENT
                    }
                ],
            },
			...(client && { client }),
        },
	);

	if (fg('forge-ui-add-public-event-methods')) {
		const allExtensions = data?.extensionContexts[0].extensions || [];
		const matchingExtension = allExtensions.find((extension: any) => {
			return extension.appId === appId && (extension.key === CLIENT_EVENTS || extension.key === CLIENT_EVENT);
		});
		const allowedRecipients = matchingExtension?.properties?.allowedRecipients ?? [];

		const filteredAllowedRecipients = allowedRecipients
			.filter((recipient: string) => recipient !== '*')
			.map((recipient: string) => recipient.split('/').pop());

		filteredAllowedRecipients.push(appId);

		return filteredAllowedRecipients;
	}

	return [appId];
}

const EVENT_PREFIX = 'forge.bridge.';

type ExtensionDetails = {
	appId: string | undefined;
	id: string;
	installationId?: string;
};

export type EmitPublicEvent = (data: { event: string, payload?: any }) => void;

export const useEmitPublicEvent = (contextId: string, extension: ExtensionDetails, client?: ApolloClient<any>) => {
	const allowedRecipients = useAllowedRecipients(contextId, extension?.appId, client);

	if (fg('forge-ui-add-public-event-methods')) {
		return (
			data: { event: string, payload?: any },
		) => {
			const { event, payload } = data;
			
			const newEvent = new CustomEvent(`${EVENT_PREFIX}${event}`, {
				detail: {
					extension,
					payload,
					allowedRecipients
				},
			});

			window.document.dispatchEvent(newEvent);
		};
	}
	return undefined;
}